import { FullGestureState } from '@use-gesture/react';
import { MutableRefObject } from 'react';
import { DraftConfiguration, Point, ViewMap } from '@/types';
import { assertDefined } from '@/utils/assertDefined';
import { subtractVectors } from '@/utils/math/subtractVectors';
import { trackError } from '@/utils/raygun';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';

const toSVGCoordinates = (point: Point, svg: SVGSVGElement): Point => {
  const matrix = assertDefined(svg.getScreenCTM());
  const svgPoint = svg.createSVGPoint();
  svgPoint.x = point.x;
  svgPoint.y = point.y;

  return svgPoint.matrixTransform(matrix);
};

let trackedInfiniteCenterPoint = false;
export const getVectors = (
  state: FullGestureState<'drag'>,
  configuration: DraftConfiguration,
  viewMap: ViewMap,
  ref: MutableRefObject<SVGSVGElement | null>,
  designCenter: MutableRefObject<Point | undefined>
): [Point?, Point?] => {
  const initial: Point = { x: state.initial[0], y: state.initial[1] };
  const current: Point = { x: state.xy[0], y: state.xy[1] };

  const svgElement = ref.current;
  if (state.first && svgElement) {
    const configurationOffset = configuration.offset;
    const viewMapOffset = viewMap.offset;
    const currentSize = getCurrentSizeOfConfiguration(configuration);

    const centerPoint = {
      x:
        (Number.isFinite(configurationOffset.x) ? configurationOffset.x : 0) +
        viewMapOffset.x +
        currentSize.width / 2,
      y:
        (Number.isFinite(configurationOffset.y) ? configurationOffset.y : 0) +
        viewMapOffset.y +
        currentSize.height / 2,
    };

    if (Number.isFinite(centerPoint.x) && Number.isFinite(centerPoint.y)) {
      designCenter.current = toSVGCoordinates(centerPoint, svgElement);
    } else if (!trackedInfiniteCenterPoint) {
      trackedInfiniteCenterPoint = true;
      trackError('Infinite center point', {
        configurationOffset,
        viewMapOffset,
        currentSize,
        scale: configuration.scale,
      });
    }
  }

  const { current: center } = designCenter;

  if (state.last) {
    designCenter.current = undefined;
  }

  if (!center) {
    return [undefined, undefined];
  }

  return [subtractVectors(initial, center), subtractVectors(current, center)];
};
